import { boundMethod } from 'autobind-decorator';
import { Container, Sprite } from 'pixi.js';

import type { CanvasComponent } from 'flow-builder/types/canvasComponent';
import type { EditorContext } from 'flow-builder/types/context';
import { HighlightType } from 'flow-builder/types/node';

import type { Relation } from '../../types/relation';
import { Layers } from '../common/props';
import {
  createTextureSpriteMap,
  setHighlightSpriteVisibility,
} from '../utils/highlightTextureMap';

import type { ActionNodeTextureMap } from './actionNodeTextures';
import { styles as nodeStyles } from './nodeStyles';
import { DragListener } from './nodeUtils';

interface Props {
  readonly node: Relation;
  readonly layers: Layers;
  readonly editorContext: EditorContext;
  readonly actionNodeTextureMap: ActionNodeTextureMap;
  readonly isHighlighted: boolean;
}

type HighlightSpriteMap = Record<HighlightType, Sprite>;

export class ActionNode implements CanvasComponent<Props> {
  public props: Props;
  public ref: Container;
  public name = 'ActionNode';

  private nodeHighlightTypes: HighlightSpriteMap;
  private isDragging = false;
  private dragListener: DragListener | null = null;

  constructor(props: Props) {
    this.props = props;

    const { actionNodeTextureMap } = props;

    const container = new Container();

    const nodeHighlightTypes = createTextureSpriteMap(
      actionNodeTextureMap,
      'Node Highlight',
    );

    this.nodeHighlightTypes = nodeHighlightTypes;

    Object.values(this.nodeHighlightTypes).forEach((sprite) => {
      sprite.position.set(nodeStyles.base.width / 2, 0);
      container.addChild(sprite);
    });

    container.interactive = true;
    container.buttonMode = true;

    this.ref = container;

    this.addClickListener();
    this.addDragListeners();
    this.drawNodePosition();
    this.drawNodeHighlight();
  }

  componentWillUnmount() {
    this.unmount();
  }

  private get dragListenerProps(): DragListener['props'] {
    const { node, editorContext } = this.props;
    const container = this.ref;

    return {
      node,
      container,
      editorContext,
      onDragStart: this.onDragStart,
      onDragMove: this.onDragMove,
      onDragEnd: this.onDragEnd,
    };
  }

  private addClickListener() {
    const container = this.ref;

    container.on('click', this.onClick);
  }

  @boundMethod
  private onClick() {
    const {
      node,
      editorContext: {
        handlers: {
          node: { onEdit },
        },
      },
    } = this.props;

    if (this.isDragging) {
      return;
    }

    onEdit(node);
  }

  private addDragListeners() {
    const props = this.dragListenerProps;

    if (!this.dragListener) {
      const dragListener = new DragListener(props);

      dragListener.addListeners();

      this.dragListener = dragListener;
    } else {
      this.dragListener.props = props;
    }
  }

  @boundMethod
  private onDragStart() {
    const { node, editorContext } = this.props;
    const {
      state: { setAttachState },
    } = editorContext;

    this.isDragging = true;
    setAttachState((state) => ({
      ...state,
      actionNodeId: node.id,
      isDragging: true,
    }));
  }

  @boundMethod
  private onDragMove() {
    const {
      editorContext: {
        handlers: {
          action: { onMove },
        },
      },
    } = this.props;

    onMove(this);
  }

  @boundMethod
  private onDragEnd() {
    const {
      editorContext: {
        handlers: {
          action: { onDrop },
        },
      },
    } = this.props;

    this.isDragging = false;

    onDrop(this);
  }

  private drawNodeHighlight() {
    const { isHighlighted, editorContext } = this.props;
    const {
      state: { highlightState },
    } = editorContext;

    const activeHighlightType = isHighlighted
      ? highlightState.type
      : HighlightType.NONE;

    setHighlightSpriteVisibility(this.nodeHighlightTypes, activeHighlightType);
  }

  private drawNodePosition() {
    const { node } = this.props;
    const { pageX, pageY } = node;
    const container = this.ref;

    container.position.set(pageX, pageY);
  }

  public isChanged(props: Props): boolean {
    const {
      editorContext,
      isHighlighted,
      node: { pageX, pageY },
    } = props;
    const {
      editorContext: propEditorContext,
      isHighlighted: propIsHighlighted,
      node: { pageX: propPageX, pageY: propPageY },
    } = this.props;
    const {
      state: { highlightState: propHighlight },
    } = propEditorContext;
    const {
      state: { highlightState: highlight },
    } = editorContext;

    const isPositionDifferent = pageX !== propPageX || pageY !== propPageY;
    const isHighlightDifferent =
      isHighlighted !== propIsHighlighted &&
      /*
       * Don't redraw if highlight type is info (purple),
       * as that'd be the same color as the default color.
       */
      (highlight.type !== propHighlight.type ||
        highlight.type !== HighlightType.INFO);

    return isPositionDifferent || isHighlightDifferent;
  }

  public redraw(props: Props): void {
    this.updatePropsWithoutRedraw(props);

    this.drawNodePosition();
    this.drawNodeHighlight();
  }

  public updatePropsWithoutRedraw(props: Props): void {
    this.props = props;

    this.addDragListeners();
  }

  public unmount(): void {
    this.ref.destroy();
    this.dragListener?.removeListeners();
  }
}
